import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";

export default function ShopSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", payment_status: null, amount: null });
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: "missing_session" });
            return;
        }
        let cancelled = false;

        const poll = async () => {
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                const data = r.data;
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", payment_status: "paid", amount: data.amount_total });
                    return;
                }
                if (data.status === "expired" || data.payment_status === "failed") {
                    setState({ status: "failed", payment_status: data.payment_status });
                    return;
                }
                attemptsRef.current += 1;
                if (attemptsRef.current >= 8) {
                    setState({ status: "timeout", payment_status: data.payment_status });
                    return;
                }
                setTimeout(poll, 2500);
            } catch {
                attemptsRef.current += 1;
                if (attemptsRef.current >= 8) {
                    setState({ status: "timeout", payment_status: "unknown" });
                    return;
                }
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="bg-black text-white min-h-screen">
            <Header />
            <main className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="shop-success-page">
                {state.status === "polling" && (
                    <>
                        <Loader2 className="w-12 h-12 mx-auto animate-spin text-streetz-yellow" />
                        <h1 className="font-display uppercase text-4xl mt-6">Checking payment…</h1>
                        <p className="text-streetz-muted mt-3">Hold on, we’re confirming with Stripe.</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 mx-auto text-streetz-yellow" />
                        <h1 className="font-display uppercase text-5xl mt-6">Payment successful</h1>
                        <p className="text-streetz-muted mt-3">Your MyStreetz merch order is locked in. We’ll email shipping info shortly.</p>
                        {state.amount != null && (
                            <p className="overline text-white mt-4">CHARGED ${(state.amount / 100).toFixed(2)} USD</p>
                        )}
                    </>
                )}
                {state.status === "failed" && (
                    <>
                        <XCircle className="w-14 h-14 mx-auto text-streetz-red" />
                        <h1 className="font-display uppercase text-5xl mt-6">Payment failed</h1>
                        <p className="text-streetz-muted mt-3">Your order was not completed. You can try again from the shop.</p>
                    </>
                )}
                {state.status === "timeout" && (
                    <>
                        <h1 className="font-display uppercase text-4xl mt-6">Still processing</h1>
                        <p className="text-streetz-muted mt-3">If you completed payment, you’ll receive an email confirmation. Refresh in a moment.</p>
                    </>
                )}
                <Link
                    to="/shop"
                    data-testid="shop-success-back"
                    className="inline-block overline bg-streetz-yellow text-black px-5 py-3 mt-8 hover:bg-white"
                >
                    BACK TO SHOP
                </Link>
            </main>
            <Footer />
        </div>
    );
}
